import { useState, useMemo } from 'react'
import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material'
import { MapCropsRow } from './map-crops-row'
import { useMapCropsStyles } from './use-map-crops-styles'

const COLUMNS = [
  { id: 'name',         label: 'Crop',            sortable: true  },
  { id: 'org',          label: 'Location',        sortable: true  },
  { id: 'maturing',     label: 'Maturing starts', sortable: true, textAlign: 'right' },
  { id: 'arrow',        label: '→',               sortable: false, arrow: true },
  { id: 'harvest',      label: 'Harvest ends',    sortable: true  },
  { id: 'relationship', label: 'Relationship',    sortable: false, width: true },
]

export const MapCropsTable = ({ crops, classifyCropDetailed, scheduleData, successionMap }) => {
  const { legendItems, RELATIONSHIP_COLUMN_WIDTH } = useMapCropsStyles()
  const [orderBy, setOrderBy] = useState('name')
  const [order, setOrder] = useState('asc')

  const handleSort = (colId) => {
    if (orderBy === colId) {
      setOrder(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setOrderBy(colId)
      setOrder('asc')
    }
  }

  const scheduleMap = useMemo(() => {
    const map = new Map()
    for (const entry of (scheduleData || [])) {
      if (entry.crop?.id) map.set(entry.crop.id, entry)
    }
    return map
  }, [scheduleData])

  const sortedCrops = useMemo(() => {
    return [...(crops || [])].sort((a, b) => {
      let aVal = '', bVal = ''

      if (orderBy === 'name') {
        aVal = (a.name || a.species_name || '').toLowerCase()
        bVal = (b.name || b.species_name || '').toLowerCase()
      } else if (orderBy === 'org') {
        aVal = (a.organization?.name || '').toLowerCase()
        bVal = (b.organization?.name || '').toLowerCase()
      } else if (orderBy === 'maturing') {
        const aPhases = scheduleMap.get(a.id)?.phases || []
        const bPhases = scheduleMap.get(b.id)?.phases || []
        aVal = aPhases.find(p => p.phase_type_code === 'maturing')?.est_started_at || ''
        bVal = bPhases.find(p => p.phase_type_code === 'maturing')?.est_started_at || ''
      } else if (orderBy === 'harvest') {
        const aPhases = scheduleMap.get(a.id)?.phases || []
        const bPhases = scheduleMap.get(b.id)?.phases || []
        aVal = aPhases.find(p => p.phase_type_code === 'mature')?.est_started_at || ''
        bVal = bPhases.find(p => p.phase_type_code === 'mature')?.est_started_at || ''
      }

      if (aVal < bVal) return order === 'asc' ? -1 : 1
      if (aVal > bVal) return order === 'asc' ? 1 : -1
      return 0
    })
  }, [crops, orderBy, order, scheduleMap])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header: subtitle + legend */}
      <Box sx={{ px: 2, pt: 2, pb: 1.5, flexShrink: 0 }}>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Interact with crop rows below to uncover intercrop relationships at the family or species level.
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {legendItems.map(item => {
            const chipColor = item.solid ? '#fff' : item.textColor
            return (
              <Chip
                key={item.label}
                size="small"
                icon={<item.Icon sx={{ fontSize: '0.7rem !important', color: `${chipColor} !important` }} />}
                label={item.label}
                sx={{
                  bgcolor: item.solid ? item.solidColor : item.bg,
                  color: chipColor,
                  border: item.solid ? 'none' : `1px solid ${item.textColor}`,
                  fontSize: '0.6rem',
                  height: 18,
                }}
              />
            )
          })}
        </Box>
      </Box>

      {/* Scrollable table */}
      <TableContainer sx={{ flex: 1 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow
              sx={{
                '& th': {
                  borderBottom: theme => `2px solid ${theme.palette.primary.light}`,
                  backgroundColor: 'background.paper',
                  position: 'sticky',
                  top: 0,
                  zIndex: 2,
                },
                boxShadow: '0 1px 12px -2px rgba(0,0,0,0.18)', // bigger shadow underneath head row
                position: 'relative', // so shadow appears over body
                zIndex: 1,
              }}
            >
              {COLUMNS.map(col => (
                <TableCell
                  key={col.id}
                  sortDirection={col.sortable && orderBy === col.id ? order : false}
                  sx={{
                    ...(col.width && { width: RELATIONSHIP_COLUMN_WIDTH, minWidth: RELATIONSHIP_COLUMN_WIDTH }),
                    ...(col.arrow && { px: 0.5, width: 20, textAlign: 'center', color: 'text.secondary', fontSize: '0.85rem' }),
                    ...(col.textAlign && { textAlign: col.textAlign })
                  }}
                >
                  {col.sortable ? (
                    <TableSortLabel
                      active={orderBy === col.id}
                      direction={orderBy === col.id ? order : 'asc'}
                      onClick={() => handleSort(col.id)}
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedCrops.map(crop => (
              <MapCropsRow
                key={crop.id}
                crop={crop}
                classifyCropDetailed={classifyCropDetailed}
                scheduleEntry={scheduleMap.get(crop.id)}
                scheduleMap={scheduleMap}
                successionMap={successionMap}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
